import React, { useState } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  Typography,
  Stack,
} from '@mui/material';
import {
  CheckCircle as SyncedIcon,
  ErrorOutline as FailedIcon,
  HourglassEmpty as PendingIcon,
  HelpOutline as UnknownIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

export type SyncStatus =
  | 'synced'
  | 'pending'
  | 'failed'
  | 'in_progress'
  | 'never'
  | string
  | null
  | undefined;

interface SyncStatusCellProps {
  status: SyncStatus;
  /** ISO 8601 string from backend (`last_sync_at` / `last_sync_success_at`). Shown in tooltip + dialog. */
  lastSyncAt?: string | null;
  /** ISO 8601 string for the last attempt — distinct from success for retry-tracking. Optional. */
  lastAttemptAt?: string | null;
  /** Free-form error from the sync run. Shown in the click-through dialog. */
  errorMessage?: string | null;
  /** Epicor's identifier once sync succeeds (order number, invoice id, etc.). Optional. */
  externalRef?: string | number | null;
  /** Display label for the dialog header (e.g. "Order ORD-202603..."). */
  entityLabel?: string;
}

const STATUS_CONFIG: Record<
  string,
  { color: 'success' | 'warning' | 'error' | 'default' | 'info'; icon: React.ReactNode; label: string }
> = {
  synced: { color: 'success', icon: <SyncedIcon fontSize="small" />, label: 'Synced' },
  pending: { color: 'warning', icon: <PendingIcon fontSize="small" />, label: 'Pending' },
  in_progress: { color: 'info', icon: <PendingIcon fontSize="small" />, label: 'In progress' },
  failed: { color: 'error', icon: <FailedIcon fontSize="small" />, label: 'Failed' },
  never: { color: 'default', icon: <UnknownIcon fontSize="small" />, label: 'Not synced' },
};

function configFor(status: SyncStatus) {
  if (!status) return STATUS_CONFIG.never;
  const key = String(status).toLowerCase();
  return STATUS_CONFIG[key] ?? { ...STATUS_CONFIG.never, label: String(status) };
}

function formatLocal(iso?: string | null): string {
  if (!iso) return '—';
  const d = dayjs(iso);
  if (!d.isValid()) return iso;
  return d.local().format('YYYY-MM-DD HH:mm:ss');
}

function formatRelative(iso?: string | null): string {
  if (!iso) return '—';
  const d = dayjs(iso);
  if (!d.isValid()) return iso;
  return d.fromNow();
}

const SyncStatusCell: React.FC<SyncStatusCellProps> = ({
  status,
  lastSyncAt,
  lastAttemptAt,
  errorMessage,
  externalRef,
  entityLabel,
}) => {
  const [open, setOpen] = useState(false);
  const cfg = configFor(status);

  // The chip is always clickable so the operator can inspect the full
  // sync state even on rows that look healthy — useful for "why isn't
  // this synced yet" investigations.
  const tooltip = lastSyncAt
    ? `${cfg.label} • ${formatRelative(lastSyncAt)} (${formatLocal(lastSyncAt)})`
    : cfg.label;

  return (
    <>
      <Tooltip title={tooltip} arrow>
        <Chip
          size="small"
          color={cfg.color}
          icon={cfg.icon as React.ReactElement}
          label={cfg.label}
          onClick={() => setOpen(true)}
          // Cursor cue that this is interactive even though it's not a
          // button. Without it operators don't realize they can click.
          sx={{ cursor: 'pointer' }}
        />
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Sync Status{entityLabel ? ` — ${entityLabel}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="caption" color="text.secondary">Status</Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  size="small"
                  color={cfg.color}
                  icon={cfg.icon as React.ReactElement}
                  label={cfg.label}
                />
              </Box>
            </Box>

            {externalRef != null && externalRef !== '' && (
              <Box>
                <Typography variant="caption" color="text.secondary">Epicor reference</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {String(externalRef)}
                </Typography>
              </Box>
            )}

            <Box>
              <Typography variant="caption" color="text.secondary">Last successful sync</Typography>
              <Typography variant="body2">
                {formatLocal(lastSyncAt)}{lastSyncAt ? ` (${formatRelative(lastSyncAt)})` : ''}
              </Typography>
            </Box>

            {lastAttemptAt != null && lastAttemptAt !== lastSyncAt && (
              <Box>
                <Typography variant="caption" color="text.secondary">Last attempt</Typography>
                <Typography variant="body2">
                  {formatLocal(lastAttemptAt)}{lastAttemptAt ? ` (${formatRelative(lastAttemptAt)})` : ''}
                </Typography>
              </Box>
            )}

            {errorMessage && (
              <Box>
                <Typography variant="caption" color="text.secondary">Error message</Typography>
                <Box
                  sx={{
                    mt: 0.5,
                    p: 1.5,
                    backgroundColor: 'grey.100',
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxHeight: 320,
                    overflowY: 'auto',
                  }}
                >
                  {errorMessage}
                </Box>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SyncStatusCell;
